import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CuentaConectada from '../integraciones/CuentaConectada';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL ||
  "https://us-central1-sistema-bks.cloudfunctions.net";

export default function MailIntegration({ user, botId, config, onUpdate, organizationId }) {
  const { t } = useTranslation('bots');
  const { t: tv } = useTranslation('validation');
  const [showSmtpForm, setShowSmtpForm] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '587',
    user: '',
    password: '',
    fromName: '',
    fromEmail: '',
  });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleConnectOAuth = async (provider) => {
    try {
      const idToken = await user.getIdToken();

      // FIX: Use correct API endpoint based on provider
      const endpoint = provider === 'gmail'
        ? `${API_URL}/api/oauth/gmail/start`
        : `${API_URL}/api/oauth/outlook/start`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          provider, // 'gmail' or 'outlook'
          organizationId,
          returnUrl: window.location.href,
          botId, // per-bot mail: scope the connection to this bot
        }),
      });

      const data = await res.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert(t('integrations.mail.connectError'));
      }
    } catch (error) {
      console.error('Error connecting mail:', error);
      alert(t('integrations.mail.connectNetworkError'));
    }
  };

  const handleSaveSMTP = async () => {
    if (!smtpConfig.host || !smtpConfig.user || !smtpConfig.fromEmail) {
      alert(tv('completeRequired'));
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/saveSmtpConfig`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          config: smtpConfig,
          organizationId,
          botId, // per-bot SMTP: scope the connection to this bot
        }),
      });

      const data = await res.json();
      if (data.success) {
        onUpdate({
          mail: {
            provider: 'smtp',
            from: {
              name: smtpConfig.fromName,
              email: smtpConfig.fromEmail,
            },
            connectedAt: new Date().toISOString(),
          },
        });
        setShowSmtpForm(false);
      }
    } catch (error) {
      console.error('Error saving SMTP:', error);
      alert(t('integrations.mail.smtpSaveError'));
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(t('integrations.mail.confirmDisconnect'))) {
      return;
    }

    try {
      const idToken = await user.getIdToken();
      await fetch(`${API_URL}/disconnectMail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ uid: user.uid, organizationId, botId }),
      });

      onUpdate({ mail: { provider: null } });
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const idToken = await user.getIdToken();
      const res = await fetch(`${API_URL}/testMailConnection`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          uid: user.uid,
          testEmail: user.email,
          organizationId,
          botId, // per-bot: test the bot's own mail account, not the org-level config
        }),
      });

      const data = await res.json();
      setTestResult(data);
    } catch (error) {
      setTestResult({ success: false, error: t('integrations.connectionError') });
    } finally {
      setTesting(false);
    }
  };

  const isConnected = config?.mail?.provider;
  const provider = config?.mail?.provider;

  return (
    <div className="integration-card">
      <div className="integration-header">
        <div className="integration-icon">📧</div>
        <div>
          <h3>{t('integrations.mail.title')}</h3>
          <p className="integration-description">
            {t('integrations.mail.description')}
          </p>
        </div>
      </div>

      {!isConnected ? (
        <div className="connection-options">
          <p className="help-text">{t('integrations.mail.selectProvider')}</p>

          <div className="provider-buttons">
            <button
              onClick={() => handleConnectOAuth('gmail')}
              className="provider-btn gmail"
            >
              <img
                src="https://www.google.com/favicon.ico"
                alt="Gmail"
                className="provider-icon"
              />
              {t('integrations.mail.connectGmail')}
            </button>
            <button
              onClick={() => handleConnectOAuth('outlook')}
              className="provider-btn outlook"
            >
              <img
                src="https://www.microsoft.com/favicon.ico"
                alt="Outlook"
                className="provider-icon"
              />
              {t('integrations.mail.connectOutlook')}
            </button>
            <button
              onClick={() => setShowSmtpForm(!showSmtpForm)}
              className="provider-btn smtp"
            >
              <span className="provider-icon">⚙️</span>
              {t('integrations.mail.configureSMTP')}
            </button>
          </div>

          {showSmtpForm && (
            <div className="smtp-form">
              <h4>{t('integrations.mail.smtpConfig')}</h4>
              <div className="form-group">
                <label>{t('integrations.mail.host')} *</label>
                <input
                  type="text"
                  value={smtpConfig.host}
                  onChange={e => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div className="form-group">
                <label>{t('integrations.mail.port')} *</label>
                <input
                  type="number"
                  value={smtpConfig.port}
                  onChange={e => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                  placeholder="587"
                />
              </div>
              <div className="form-group">
                <label>{t('integrations.mail.user')} *</label>
                <input
                  type="email"
                  value={smtpConfig.user}
                  onChange={e => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                  placeholder={t('integrations.mail.placeholderUser')}
                />
              </div>
              <div className="form-group">
                <label>{t('integrations.mail.password')} *</label>
                <input
                  type="password"
                  value={smtpConfig.password}
                  onChange={e => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                  placeholder="••••••••"
                />
              </div>
              <div className="form-group">
                <label>{t('integrations.mail.senderName')}</label>
                <input
                  type="text"
                  value={smtpConfig.fromName}
                  onChange={e => setSmtpConfig({ ...smtpConfig, fromName: e.target.value })}
                  placeholder={t('integrations.mail.placeholderSenderName')}
                />
              </div>
              <div className="form-group">
                <label>{t('integrations.mail.senderEmail')} *</label>
                <input
                  type="email"
                  value={smtpConfig.fromEmail}
                  onChange={e => setSmtpConfig({ ...smtpConfig, fromEmail: e.target.value })}
                  placeholder={t('integrations.mail.placeholderSenderEmail')}
                />
              </div>
              <button onClick={handleSaveSMTP} className="btn-save">
                {t('integrations.mail.saveSMTP')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="connected-section">
          <div className="status-badge connected">
            {t('integrations.connected')} -{' '}
            {provider === 'gmail'
              ? 'Gmail'
              : provider === 'outlook'
              ? 'Outlook'
              : 'SMTP'}
          </div>

          {/* QUÉ CUENTA está conectada — SÓLO para Gmail/Outlook.
              ⚠️ SE USA `from.email` Y NO `accountName`. `accountName` vive en otro
              documento (`integrations/gmail`) que este panel no carga, y hoy vale lo
              MISMO: el callback de OAuth deriva los dos del email de la cuenta
              (api.express.js:489 para Gmail, :731 para Outlook).

              ⚠️⚠️ DEPENDENCIA FRÁGIL, ANOTADA A PROPÓSITO: si algún día el remitente se
              vuelve EDITABLE, `from.email` deja de ser la cuenta conectada y esta
              etiqueta pasa a MENTIR. En ese momento hay que leer `accountName` del doc
              `integrations/{provider}` — que implica cargarlo aquí.

              SMTP queda fuera adrede: ahí `from` es configuración pura y llamarlo
              "cuenta conectada" sería el error inverso al que esto arregla. */}
          {(config.mail.provider === 'gmail' || config.mail.provider === 'outlook') && (
            <CuentaConectada
              variante="bot"
              etiqueta={t('integrations.connectedAsLabel')}
              cuenta={config.mail.from?.email}
            />
          )}

          {config.mail.from && (
            <p className="connection-meta">
              {t('integrations.mail.sender')}: {config.mail.from.name} &lt;{config.mail.from.email}&gt;
            </p>
          )}

          <div className="actions">
            <button
              onClick={handleTest}
              disabled={testing}
              className="btn-test"
            >
              {testing ? t('integrations.mail.sending') : t('integrations.mail.sendTest')}
            </button>
            <button onClick={handleDisconnect} className="btn-disconnect">
              {t('integrations.disconnect')}
            </button>
          </div>

          {testResult && (
            <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
              {testResult.success
                ? t('integrations.mail.testSent', { email: user.email })
                : `${t('integrations.connectionFailed')}: ${testResult.error}`}
            </div>
          )}
        </div>
      )}

      <style>{`
        .integration-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .integration-header {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .integration-icon {
          font-size: 2.5rem;
        }

        .integration-card h3 {
          margin: 0 0 0.25rem 0;
          color: #2d3748;
          font-size: 1.25rem;
        }

        .integration-description {
          color: #718096;
          font-size: 0.875rem;
          margin: 0;
        }

        .connection-options {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .help-text {
          color: #4a5568;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .provider-buttons {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }

        .provider-btn {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          background: white;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .provider-btn:hover {
          border-color: #667eea;
          background: #f7fafc;
        }

        .provider-icon {
          width: 20px;
          height: 20px;
        }

        .smtp-form {
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          margin: 1rem 0;
        }

        .smtp-form h4 {
          margin: 0 0 1rem 0;
          color: #2d3748;
        }

        .form-group {
          margin-bottom: 1rem;
        }

        .form-group label {
          display: block;
          font-size: 0.875rem;
          font-weight: 600;
          color: #2d3748;
          margin-bottom: 0.25rem;
        }

        .form-group input {
          width: 100%;
          padding: 0.5rem;
          border: 1px solid #cbd5e0;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .form-group input:focus {
          outline: none;
          border-color: #667eea;
        }

        .btn-save {
          width: 100%;
          padding: 0.75rem;
          background: #667eea;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          cursor: pointer;
          margin-top: 0.5rem;
        }

        .btn-save:hover {
          background: #5568d3;
        }

        .info-box {
          background: #fff5e5;
          border: 1px solid #ffd580;
          border-radius: 6px;
          padding: 1rem;
          color: #8b5a00;
          font-size: 0.875rem;
        }

        .connected-section {
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .status-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        .status-badge.connected {
          background: #c6f6d5;
          color: #22543d;
        }

        .connection-meta {
          font-size: 0.875rem;
          color: #718096;
          margin: 0.5rem 0;
        }

        .actions {
          display: flex;
          gap: 0.75rem;
          margin-top: 1.5rem;
        }

        .btn-test,
        .btn-disconnect {
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-test {
          background: #667eea;
          color: white;
        }

        .btn-test:hover:not(:disabled) {
          background: #5568d3;
        }

        .btn-test:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-disconnect {
          background: white;
          color: #e53e3e;
          border: 1px solid #fc8181;
        }

        .btn-disconnect:hover {
          background: #fff5f5;
        }

        .test-result {
          margin-top: 1rem;
          padding: 0.75rem;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .test-result.success {
          background: #f0fff4;
          color: #22543d;
          border: 1px solid #9ae6b4;
        }

        .test-result.error {
          background: #fff5f5;
          color: #742a2a;
          border: 1px solid #fc8181;
        }
      `}</style>
    </div>
  );
}
